import React from 'react';
import { Thumb } from '../ui';
import { REVIEW_SECTIONS } from '../data/reviewSchema';
import { tr } from '../i18n';
import {
  bestQuoted,
  contactLabel,
  dateLabel,
  landed,
  money,
  paidAtBooth,
  priceLine,
  primaryContact,
  riskFlags,
  supplierDecision,
  supplierRating,
  supplierSort,
} from '../rules';

/**
 * Report sheets (spec § 8.1) — the A4-like pages shown in the in-app viewer.
 *
 * These are HTML, not PDF, and that is the better path for this app: the
 * browser renders Chinese supplier names correctly with its own fonts, and
 * "Print → Save as PDF" produces a perfect PDF including CJK. The jsPDF export
 * (reports/pdf.js) is Latin-only and exists for the one-tap download case.
 *
 * The @media print rules in fairTrackerReports.css are what make Print produce
 * clean pages rather than a screenshot of the app.
 */

/* ── Supplier sheet ─────────────────────────────────────────────────────── */

export function SupplierSheet({ supplier, fair }) {
  const l = landed(supplier);
  const best = bestQuoted(supplier, fair);
  const products = supplier.products || [];
  const photos = (supplier.photos || []).slice(0, 5);
  const openFollowUps = (supplier.followUps || []).filter((f) => f.status !== 'Done');
  const review = supplier.review || {};
  const flags = riskFlags(supplier);

  const reviewRows = REVIEW_SECTIONS.flatMap((s) =>
    s.fields
      .filter(([k]) => review[k] !== undefined && String(review[k] || '').trim() !== '')
      .map(([k, label]) => [label, review[k]]),
  );

  return (
    <article className="ft-sheet">
      <header className="ft-sheet-head">
        <div>
          <h1>{supplier.company}</h1>
          <p>{(fair && fair.name) || ''}</p>
        </div>
        <div className="ft-sheet-brand">
          <b>{tr("Fair Tracker")}</b>
          <span>BeeNexa</span>
        </div>
      </header>

      <dl className="ft-sheet-meta">
        <div>
          <dt>{tr("Visited")}</dt>
          <dd>{supplier.createdAt ? new Date(supplier.createdAt).toLocaleDateString() : '—'}</dd>
        </div>
        <div>
          <dt>{tr("Products")}</dt>
          <dd>{products.length}</dd>
        </div>
        <div>
          <dt>{tr("Contacts")}</dt>
          <dd>{(supplier.contacts || []).length}</dd>
        </div>
      </dl>

      <div className="ft-sheet-tiles">
        <Tile label={tr("Decision")} value={supplierDecision(supplier)} />
        <Tile label={tr("Rating")} value={supplierRating(supplier) ? `${supplierRating(supplier)}/5` : '—'} />
        <Tile label={tr("Best price")} value={best != null ? money(best) : '—'} />
        <Tile label={tr("MOQ")} value={(products[0] && products[0].moq) || '—'} />
        <Tile label={tr("Landed / unit")} value={l.perUnit != null ? money(l.perUnit) : '—'} />
        <Tile label={tr("Final")} value={supplier.decision || '—'} />
      </div>

      {photos.length > 0 && (
        <div className="ft-sheet-photos">
          {photos.map((ph) => (
            <Thumb key={ph.id} photo={ph} size={92} rounded={false} alt={ph.tag || ''} />
          ))}
        </div>
      )}

      {products.length > 0 && (
        <>
          <h2>{tr("Products")}</h2>
          <table className="ft-sheet-table">
            <thead>
              <tr>
                <th>{tr("Product")}</th>
                <th>{tr("Price")}</th>
                <th>{tr("MOQ")}</th>
                <th>{tr("Lead time")}</th>
                <th>{tr("Payment")}</th>
                <th>{tr("Sample")}</th>
                <th>{tr("Rating")}</th>
                <th>{tr("Next step")}</th>
              </tr>
            </thead>
            <tbody>
              {products.map((prod) => (
                <tr key={prod.id}>
                  <td>{prod.name}</td>
                  <td>{priceLine(prod, fair) || '—'}</td>
                  <td>{prod.moq || '—'}</td>
                  <td>{prod.productionTime || '—'}</td>
                  <td>{prod.paymentTerms || '—'}</td>
                  <td>{prod.sample || '—'}</td>
                  <td>{prod.rating ? `${prod.rating}/5` : '—'}</td>
                  <td>{prod.nextStep !== 'None' ? prod.nextStep : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {products.map((prod) => {
            const notes = [
              (prod.tiers || []).length
                ? `Tiers: ${prod.tiers.map((t) => `${t.qty} @ ${t.price}`).join(', ')}`
                : null,
              prod.cartonL || prod.cartonW || prod.cartonH
                ? `Carton: ${prod.cartonL || '?'}×${prod.cartonW || '?'}×${prod.cartonH || '?'} ${prod.lenUnit}${
                    prod.cartonWeight ? `, ${prod.cartonWeight}${prod.wtUnit}` : ''
                  }${prod.unitsPerCarton ? `, ${prod.unitsPerCarton}/carton` : ''}`
                : null,
              prod.fobPort ? `FOB: ${prod.fobPort}` : null,
              prod.cert ? `USA cert: ${prod.cert}${(prod.certTypes || []).length ? ` (${prod.certTypes.join(', ')})` : ''}` : null,
              prod.revisit ? `Revisit: ${prod.revisitReason || 'yes'}` : null,
              prod.notes || null,
            ].filter(Boolean);
            if (!notes.length) return null;
            return (
              <div className="ft-sheet-note" key={prod.id}>
                <b>{prod.name}</b>
                <ul>
                  {notes.map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
              </div>
            );
          })}
        </>
      )}

      {(supplier.contacts || []).length > 0 && (
        <>
          <h2>{tr("Contacts")}</h2>
          {supplier.contacts.map((c) => (
            <div className="ft-sheet-contact" key={c.id}>
              <div>
                <b>
                  {contactLabel(c)}
                  {c.primary ? ' (primary)' : ''}
                </b>
                <dl className="ft-sheet-kv">
                  {[
                    ['Phone', c.phone],
                    ['WeChat', c.wechat],
                    ['WhatsApp', c.whatsapp],
                    ['Email', c.email],
                    ['Website', c.website],
                  ]
                    .filter(([, v]) => v)
                    .map(([k, v]) => (
                      <React.Fragment key={k}>
                        <dt>{k}</dt>
                        <dd>{v}</dd>
                      </React.Fragment>
                    ))}
                </dl>
              </div>
              {(c.photos || [])
                .filter((ph) => ph.tag === 'Business Card')
                .slice(0, 1)
                .map((ph) => (
                  <Thumb key={ph.id} photo={ph} size={110} rounded={false} alt="Business card" />
                ))}
            </div>
          ))}
        </>
      )}

      {paidAtBooth(supplier) > 0 && (
        <p className="ft-sheet-paid">
          {tr("Paid at booth:")} <b>{money(paidAtBooth(supplier))}</b>
        </p>
      )}

      {supplier.chinaNotes && (
        <>
          <h2>{tr("Fair notes")}</h2>
          <p className="ft-sheet-prewrap">{supplier.chinaNotes}</p>
        </>
      )}

      {reviewRows.length > 0 && (
        <>
          <h2>{tr("Final review")}</h2>
          {flags.length > 0 && (
            <p className="ft-sheet-flags">Risk flags: {flags.join('; ')}</p>
          )}
          <dl className="ft-sheet-kv ft-sheet-kv-2col">
            {reviewRows.map(([label, value]) => (
              <React.Fragment key={label}>
                <dt>{label}</dt>
                <dd>{String(value)}</dd>
              </React.Fragment>
            ))}
          </dl>
        </>
      )}

      {openFollowUps.length > 0 && (
        <>
          <h2>{tr("Open follow-ups")}</h2>
          <ul className="ft-sheet-list">
            {openFollowUps.map((f) => (
              <li key={f.id}>
                <b>{f.type}</b>
                {[dateLabel(f.date), f.time, f.status, f.notes].filter(Boolean).join(' · ')}
              </li>
            ))}
          </ul>
        </>
      )}
    </article>
  );
}

function Tile({ label, value }) {
  return (
    <div className="ft-sheet-tile">
      <span>{label}</span>
      <b>{value}</b>
    </div>
  );
}

/* ── Shortlist report: a comparison table, then one sheet per finalist ──── */

export function ShortlistSheets({ suppliers, fair }) {
  const finalists = suppliers.filter((s) => s.inShortlist);

  return (
    <>
      <article className="ft-sheet">
        <header className="ft-sheet-head">
          <div>
            <h1>{tr("Final shortlist")}</h1>
            <p>
              {[fair && fair.name, fair && fair.location, `${finalists.length} finalists`]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
          <div className="ft-sheet-brand">
            <b>{tr("Fair Tracker")}</b>
            <span>BeeNexa</span>
          </div>
        </header>

        <table className="ft-sheet-table">
          <thead>
            <tr>
              <th>{tr("Supplier")}</th>
              <th>{tr("Product")}</th>
              <th>{tr("Quoted")}</th>
              <th>{tr("Landed / unit")}</th>
              <th>{tr("MOQ")}</th>
              <th>{tr("Cert")}</th>
              <th>{tr("Risks")}</th>
              <th>{tr("Decision")}</th>
            </tr>
          </thead>
          <tbody>
            {finalists.map((s) => {
              const prod = (s.products || []).find((x) => x.decision === 'Strong') || (s.products || [])[0];
              const l = landed(s);
              return (
                <tr key={s.id}>
                  <td>{s.company}</td>
                  <td>{(prod && prod.name) || '—'}</td>
                  <td>{prod ? priceLine(prod, fair) || '—' : '—'}</td>
                  <td>{l.perUnit != null ? money(l.perUnit) : '—'}</td>
                  <td>{(prod && prod.moq) || '—'}</td>
                  <td>{(s.review || {}).c_status || (prod && prod.cert) || '—'}</td>
                  <td>{riskFlags(s).length || '0'}</td>
                  <td>{s.decision || supplierDecision(s)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </article>

      {finalists.map((s) => (
        <SupplierSheet key={s.id} supplier={s} fair={fair} />
      ))}
    </>
  );
}

/* ── Fair report: the whole fair, then one sheet per supplier ───────────── */

export function FairSheets({ suppliers, fair }) {
  const sorted = [...suppliers].sort(supplierSort);

  return (
    <>
      <article className="ft-sheet ft-sheet-cover">
        <header className="ft-sheet-head">
          <div>
            <h1>{(fair && fair.name) || 'Fair report'}</h1>
            <p>
              {[fair && fair.location, fair && fair.dates, `${suppliers.length} suppliers`]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
          <div className="ft-sheet-brand">
            <b>{tr("Fair Tracker")}</b>
            <span>BeeNexa</span>
          </div>
        </header>

        <table className="ft-sheet-table">
          <thead>
            <tr>
              <th>{tr("Supplier")}</th>
              <th>{tr("Product")}</th>
              <th>{tr("Price")}</th>
              <th>{tr("MOQ")}</th>
              <th>{tr("Rating")}</th>
              <th>{tr("Next")}</th>
              <th>{tr("Contact")}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((s) => {
              const prod = (s.products || [])[0];
              const contact = primaryContact(s);
              return (
                <tr key={s.id}>
                  <td>{s.company}</td>
                  <td>{(prod && prod.name) || '—'}</td>
                  <td>{prod ? priceLine(prod, fair) || '—' : '—'}</td>
                  <td>{(prod && prod.moq) || '—'}</td>
                  <td>{supplierRating(s) ? `${supplierRating(s)}/5` : '—'}</td>
                  <td>{prod && prod.nextStep !== 'None' ? prod.nextStep : '—'}</td>
                  <td>
                    {contact ? contactLabel(contact, '—') : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </article>

      {sorted.map((s) => (
        <SupplierSheet key={s.id} supplier={s} fair={fair} />
      ))}
    </>
  );
}
